import React from "react";
import { Check, Loader2 } from "lucide-react";
import { CircularProgress } from "@heroui/react";
import { useEnrollmentBatchToastStore } from "@/features/campaigns/stores/enrollmentBatchToastStore";
import FloatingProgressToast from "@/shared/components/ui/Toast/FloatingProgressToast";
import EnrollmentContentProgressBody from "@/features/campaigns/components/EnrollmentContentProgressBody";
import { getEnrollmentProgressPercent } from "@/features/campaigns/utils/enrollmentContentProgress";

const MODE_COPY = {
  account: {
    activeTitle: "Generating account information...",
    completeTitle: "Account generation complete",
    activeSubtitle: "This will continue in the background.",
    completeSubtitle: "Account research is ready.",
  },
  campaign: {
    activeTitle: "Enrolling Leads & Generating Profiles...",
    completeTitle: "Enrollment complete",
    activeSubtitle: "This will continue in the background.",
    completeSubtitle: "Content and profiles are ready.",
  },
  both: {
    activeTitle: "Enrolling Leads & Generating Profiles...",
    completeTitle: "Enrollment complete",
    activeSubtitle: "This will continue in the background.",
    completeSubtitle: "Content and research are ready.",
  },
};

export default function EnrollmentBatchProgressToastPanel({ onDismiss }) {
  const {
    mode,
    total,
    contentDone,
    profileDone,
    accountDone,
    skipped,
    failed,
    accountSummary,
    isComplete,
  } = useEnrollmentBatchToastStore();

  const batchMode = mode || "campaign";
  const copy = MODE_COPY[batchMode] ?? MODE_COPY.campaign;

  const pct = getEnrollmentProgressPercent({
    total,
    contentDone,
    profileDone,
    accountDone,
    mode: batchMode,
  });
  const safeTotal = Number(total || 0);
  const skippedCount = skipped?.length ?? 0;
  const hasIssues = skippedCount > 0 || Number(failed || 0) > 0;
  const title = isComplete ? copy.completeTitle : copy.activeTitle;
  const subtitle = isComplete ? copy.completeSubtitle : copy.activeSubtitle;
  const icon = isComplete ? (
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-success-500">
      <Check size={18} className="text-white" strokeWidth={2.5} />
    </span>
  ) : (
    <div className="shrink-0 w-9 h-9 rounded-lg bg-[#DBF5F9] dark:bg-secondary/20 flex items-center justify-center">
      <CircularProgress
        aria-label="Enrollment progress"
        value={pct}
        showValueLabel={false}
        strokeWidth={4}
        classNames={{
          svg: "w-7 h-7",
          indicator: "stroke-secondary",
          track: "stroke-[#D5D7DA] dark:stroke-gray-700",
        }}
      />
    </div>
  );

  return (
    <FloatingProgressToast
      icon={icon}
      title={title}
      subtitle={subtitle}
      onDismiss={onDismiss}
      dismissLabel="Dismiss enrollment progress"
    >
      <EnrollmentContentProgressBody
        mode={batchMode}
        total={safeTotal}
        contentDone={contentDone}
        profileDone={profileDone}
        accountDone={accountDone}
        accountSummary={accountSummary}
        percent={pct}
      />
      {hasIssues ? (
        <div className="px-4 pb-3 -mt-1">
          <p className="flex items-center gap-1 text-[11px] text-gray-500">
            <Loader2 size={12} className="shrink-0" />
            {skippedCount > 0 ? `${skippedCount} skipped` : `${failed} failed`}
          </p>
        </div>
      ) : null}
    </FloatingProgressToast>
  );
}
